import { useState, useCallback, useEffect, useRef } from "react";

// Must match the products panel's min-width (see .pcp-products-panel in
// ProductsPage.css) — it's the floor below which nothing can shrink.
const PRODUCTS_PANEL_MIN_WIDTH = 200;

const CATEGORY_WIDTH_STORAGE_KEY = "pcp-category-panel-width";
const CART_WIDTH_STORAGE_KEY = "pcp-cart-panel-width";

// Screen-size tier the caps and defaults are keyed on. When the window
// crosses into a different tier (e.g. tab dragged from a monitor to a
// laptop), saved widths from the old tier may not fit, so we reset to the
// new tier's defaults.
const getScreenTier = () =>
  window.innerWidth <= 1024 ? "small" : window.innerWidth <= 1440 ? "medium" : "large";

const getDefaultCategoryWidth = () => (window.innerWidth <= 1024 ? 220 : 250);
const getDefaultCartWidth = () => (window.innerWidth <= 1024 ? 300 : 400);

// Restore a previously dragged width, clamped to the given bounds so a value
// saved on a big monitor can't blow past the caps of a smaller screen. Falls
// back to the default when nothing was saved yet.
const getStoredWidth = (storageKey, fallback, min, max) => {
  const stored = parseInt(localStorage.getItem(storageKey), 10);
  if (Number.isNaN(stored)) return fallback;
  return Math.max(min, Math.min(stored, max));
};

export const useResizablePanels = () => {
  const getInitialCategoryWidth = () =>
    getStoredWidth(
      CATEGORY_WIDTH_STORAGE_KEY,
      getDefaultCategoryWidth(),
      150,
      getCategoryMaxWidth()
    );
  const getInitialCartWidth = () =>
    getStoredWidth(
      CART_WIDTH_STORAGE_KEY,
      getDefaultCartWidth(),
      250,
      getCartMaxWidth()
    );

  // Drag ceilings scale with screen size so the products area never gets
  // crowded out: small laptops (≤1024px), regular laptops (≤1440px), and
  // larger monitors above that.
  const getCategoryMaxWidth = () => {
    if (window.innerWidth <= 1024) return 236;
    if (window.innerWidth <= 1440) return 302;
    return 350;
  };
  const getCartMaxWidth = () => {
    if (window.innerWidth <= 1024) return 352;
    if (window.innerWidth <= 1440) return 487;
    return 600;
  };

  const [categoryPanelWidth, setCategoryPanelWidth] = useState(getInitialCategoryWidth);
  const [cartPanelWidthState, setCartPanelWidthState] = useState(getInitialCartWidth);

  const screenTierRef = useRef(getScreenTier());

  useEffect(() => {
    const onWindowResize = () => {
      const tier = getScreenTier();
      if (tier === screenTierRef.current) return;
      screenTierRef.current = tier;

      const categoryWidth = getDefaultCategoryWidth();
      const cartWidth = getDefaultCartWidth();
      setCategoryPanelWidth(categoryWidth);
      setCartPanelWidthState(cartWidth);
      localStorage.setItem(CATEGORY_WIDTH_STORAGE_KEY, String(categoryWidth));
      localStorage.setItem(CART_WIDTH_STORAGE_KEY, String(cartWidth));
    };

    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  // Measure, on every mouse move, how many pixels the products panel can still
  // give up before hitting its minimum. Growing the dragged panel is allowed
  // only by that amount, so once the products panel is at its minimum the drag
  // simply stops instead of pushing the cart off-screen. Reading the rendered
  // width (offsetWidth) instead of recomputing from state means borders,
  // box-sizing and the app drawer are all accounted for automatically.
  const getProductsPanelRoom = (layoutEl) => {
    const productsEl = layoutEl?.querySelector(".pcp-products-panel");
    if (!productsEl) return Infinity;
    return Math.max(productsEl.offsetWidth - PRODUCTS_PANEL_MIN_WIDTH, 0);
  };

  const startResizingCategory = useCallback((mouseDownEvent) => {
    mouseDownEvent.preventDefault();
    const startX = mouseDownEvent.clientX;
    const startWidth = categoryPanelWidth;
    const layoutEl = mouseDownEvent.currentTarget.parentElement;

    const onMouseMove = (mouseMoveEvent) => {
      const desiredWidth = startWidth + (mouseMoveEvent.clientX - startX);
      setCategoryPanelWidth((current) => {
        const maxWidth = Math.min(getCategoryMaxWidth(), current + getProductsPanelRoom(layoutEl));
        const nextWidth = Math.max(150, Math.min(desiredWidth, maxWidth));
        localStorage.setItem(CATEGORY_WIDTH_STORAGE_KEY, String(nextWidth));
        return nextWidth;
      });
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [categoryPanelWidth]);

  const startResizingCart = useCallback((mouseDownEvent) => {
    mouseDownEvent.preventDefault();
    const startX = mouseDownEvent.clientX;
    const startWidth = cartPanelWidthState;
    const layoutEl = mouseDownEvent.currentTarget.parentElement;

    const onMouseMove = (mouseMoveEvent) => {
      const desiredWidth = startWidth - (mouseMoveEvent.clientX - startX);
      setCartPanelWidthState((current) => {
        const maxWidth = Math.min(getCartMaxWidth(), current + getProductsPanelRoom(layoutEl));
        const nextWidth = Math.max(250, Math.min(desiredWidth, maxWidth));
        localStorage.setItem(CART_WIDTH_STORAGE_KEY, String(nextWidth));
        return nextWidth;
      });
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [cartPanelWidthState]);

  return {
    categoryPanelWidth,
    cartPanelWidthState,
    startResizingCategory,
    startResizingCart,
  };
};
